/**
 * Mobile Bottom Navigation Component
 * 
 * Provides a mobile-optimized bottom navigation bar
 * that appears on smaller screens.
 */

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faStore,
  faUsers,
  faCoins,
  faUser,
  faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/CombinedAuthContext';
import { usePlans } from '../../../contexts/PlansContext';
import { useAdmin } from '../../../contexts/AdminContext';
import './MobileNav.css';

/**
 * Navigation items configuration
 */
const getNavItems = (isAuthenticated, isMember) => {
  const items = [
    {
      path: '/',
      label: 'Home',
      icon: faHome,
      exact: true,
      showAlways: true
    },
    {
      path: '/business-listings',
      label: 'Listings',
      icon: faStore,
      showAlways: true
    }
  ];

  // Member-only items
  if (isAuthenticated && isMember) {
    items.push({
      path: '/roommate-finder',
      label: 'Roommates',
      icon: faUsers,
      requiresMember: true
    });
    
    items.push({
      path: '/tokens',
      label: 'Tokens',
      icon: faCoins,
      requiresMember: true
    });
  }

  // Account/More
  items.push({
    path: isAuthenticated ? '/account' : '/login',
    label: isAuthenticated ? 'Account' : 'Login',
    icon: isAuthenticated ? faUser : faUser,
    showAlways: true
  });

  return items;
};

/**
 * More menu items (overflow)
 */
const getMoreItems = (isAuthenticated, isAdmin, isBusinessOwner) => {
  const items = [
    {
      path: '/support',
      label: 'Help & Support',
    }
  ];

  // Business Dashboard for business owners
  if (isBusinessOwner) {
    items.push({
      path: '/account/business',
      label: 'Business Profile'
    });
    items.push({
      path: '/account/analytics',
      label: 'Business Analytics'
    });
    items.push({
      path: '/business-dashboard',
      label: 'Business Dashboard'
    });
  }

  if (isAdmin) {
    items.push({
      path: '/admin',
      label: 'Admin Dashboard'
    });
  }

  return items;
};

/**
 * Mobile Bottom Navigation Component
 */
export function MobileBottomNav() {
  const { isAuthenticated } = useAuth();
  const plans = usePlans();
  const { isAdmin } = useAdmin();
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Paid-only: the member nav items (Roommates, Tokens) are premium surfaces —
  // roommateJobs is a Traveler Plus perk and Doubloon packs require a paid plan.
  // isMember() is true for free Deckhands, so it must not gate these.
  const hasMember =
    typeof plans?.hasPaidSubscription === 'function' ? plans.hasPaidSubscription() : false;
  const hasBusinessOwner = typeof plans?.isBusinessOwner === 'function' ? plans.isBusinessOwner() : false;

  // Get navigation items based on auth state
  const navItems = getNavItems(isAuthenticated, hasMember);
  const moreItems = getMoreItems(isAuthenticated, isAdmin, hasBusinessOwner);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close more menu on route change
  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  // Close more menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMore && !e.target.closest('.mobile-nav-more-menu')) {
        setShowMore(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMore]);

  return (
    <>
      {/* Spacer to prevent content being hidden behind nav */}
      <div className="mobile-nav-spacer" />
      
      <nav 
        className={`mobile-bottom-nav ${isVisible ? '' : 'mobile-nav-hidden'}`}
        aria-label="Mobile navigation"
      >
        <div className="mobile-nav-items">
          {navItems.slice(0, 4).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `mobile-nav-item ${isActive ? 'active' : ''}`
              }
              end={item.exact}
            >
              <FontAwesomeIcon icon={item.icon} className="mobile-nav-icon" />
              <span className="mobile-nav-label">{item.label}</span>
            </NavLink>
          ))}
          
          {/* More button for overflow items */}
          {moreItems.length > 0 && (
            <button
              className={`mobile-nav-item mobile-nav-more ${showMore ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMore(!showMore);
              }}
              aria-expanded={showMore}
              aria-haspopup="true"
            >
              <FontAwesomeIcon icon={faEllipsisH} className="mobile-nav-icon" />
              <span className="mobile-nav-label">More</span>
            </button>
          )}
        </div>

        {/* More menu popup */}
        {showMore && (
          <div className="mobile-nav-more-menu">
            {moreItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className="mobile-nav-more-item"
              >
                {item.label}
              </NavLink>
            ))}
            
            {/* Last nav item if we have 5+ items */}
            {navItems.length > 4 && (
              <NavLink
                to={navItems[4].path}
                className="mobile-nav-more-item"
              >
                {navItems[4].label}
              </NavLink>
            )}
          </div>
        )}
      </nav>
    </>
  );
}

/**
 * Hook to detect if mobile navigation should be shown
 */
export function useMobileNav() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export default MobileBottomNav;
